import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ImageWithFallback } from "../ImageWithFallback";
import avatar from "../../assets/962ef5add980d65c7efa5147f6451803674674bf.png";
import { BRAND } from "../../lib/brand";
import { FOUNDER } from "../../data/agencyData";

export function FounderSection() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="relative w-full px-4 md:px-[116px] py-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full grid gap-10 lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-20 lg:items-center"
      >
        {/* Portrait */}
        <div className="flex flex-col items-start gap-5">
          <div className="relative w-[180px] md:w-[240px] lg:w-full">
            {/* Dotted ring around the illustration, turning slowly (see .gc-dotring).
                Sits just outside the portrait; a full circle of dots so rotation
                reads as the dots travelling round the arc. */}
            <span
              className="gc-dotring pointer-events-none absolute -inset-[14px] rounded-full border-2 border-dotted"
              style={{ borderColor: `${BRAND.navy}59` }}
              aria-hidden="true"
            />
            <ImageWithFallback
              src={avatar}
              alt="Olga Molina, founder of GeekCow Creative Studio"
              className="w-full h-auto rounded-full shadow-[0_18px_40px_-18px_rgba(0,62,91,0.55)]"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-6">
          <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">
            {FOUNDER.eyebrow}
          </p>

          <div className="flex flex-col gap-4">
            <h2 className="font-black text-[36px] md:text-[56px] leading-[1.05] tracking-tight text-[#141414]">
              {FOUNDER.name}
            </h2>
            <p
              className="font-mono text-[13px] md:text-[15px] tracking-[1.5px] uppercase"
              style={{ color: BRAND.navy }}
            >
              {FOUNDER.role}
            </p>
          </div>

          <p className="text-[16px] md:text-[19px] leading-[1.65] text-[#666] max-w-[860px]">{FOUNDER.body}</p>

          {/* The interim door — deliberately addressed to a different buyer.
              The line leads into the button, sitting above it, so the reader
              knows who the button is for before they reach it. */}
          <div className="mt-2 flex flex-col items-start gap-5">
            <p className="text-[15px] md:text-[17px] leading-[1.55] text-[#666] max-w-[560px]">
              {FOUNDER.cta.lead} {FOUNDER.cta.note}
            </p>
            <Link
              to="/olga"
              className="group inline-flex w-fit shrink-0 items-center gap-3 rounded-full border-2 border-[#141414] px-7 py-4 font-bold text-[13px] tracking-[2px] uppercase text-[#003e5b] shadow-[0_6px_0_-1px_rgba(0,0,0,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_9px_0_-1px_rgba(0,0,0,0.22)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#003e5b]/30"
              style={{ background: BRAND.greenBright }}
            >
              {FOUNDER.cta.label}
              <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">
                →
              </span>
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
